// Admin Authentication Manager
// Handles JWT tokens and authentication state

export class AuthManager {
  constructor() {
    this.authKey = 'adminAuth';
  }

  // Check if user is authenticated
  isAuthenticated() {
    const authData = this.getAuthData();
    if (!authData) return false;

    // Check if token is expired (simple check - in production you'd validate JWT properly)
    const tokenAge = Date.now() - authData.timestamp;
    const maxAge = 24 * 60 * 60 * 1000; // 24 hours
    
    return tokenAge < maxAge;
  }

  // Get stored authentication data
  getAuthData() {
    try {
      const stored = localStorage.getItem(this.authKey);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error parsing auth data:', error);
      return null;
    }
  }

  // Get JWT token for API calls
  getAuthToken() {
    const authData = this.getAuthData();
    return authData?.idToken || null;
  }

  // Get tenant ID
  getTenantId() {
    const authData = this.getAuthData();
    return authData?.tenantId || null;
  }

  // Get phone number
  getPhone() {
    const authData = this.getAuthData();
    return authData?.phone || null;
  }

  // Store authentication data
  setAuthData(authData) {
    localStorage.setItem(this.authKey, JSON.stringify(authData));
  }

  // Clear authentication
  clearAuth() {
    localStorage.removeItem(this.authKey);
  }

  // Get headers for authenticated API calls
  getAuthHeaders() {
    const token = this.getAuthToken();
    const tenantId = this.getTenantId();
    
    const headers = {
      'Content-Type': 'application/json'
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (tenantId) {
      headers['X-Tenant-ID'] = tenantId;
    }

    return headers;
  }

  // Create authenticated fetch wrapper
  async authenticatedFetch(url, options = {}) {
    const headers = {
      ...this.getAuthHeaders(),
      ...(options.headers || {})
    };

    return fetch(url, {
      ...options,
      headers
    });
  }
}

// Singleton instance
export const authManager = new AuthManager();

// Helper functions
export const isAdminAuthenticated = () => authManager.isAuthenticated();
export const getAuthToken = () => authManager.getAuthToken();
export const getTenantId = () => authManager.getTenantId();
export const clearAdminAuth = () => authManager.clearAuth();